import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Search, Copy, Check, Maximize2, Minimize2, Heart, Leaf, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import ofnrData from '../data/ofnr_trainer_data.json';

// Reusable Feelings & Needs Helper Component
// Can be used as a slide-out panel or modal

export default function FeelingsNeedsHelper({ isOpen, onClose, mode = 'panel' }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('satisfied'); // 'satisfied', 'unsatisfied', 'needs'
  const [expandedCategories, setExpandedCategories] = useState({});
  const [copiedWord, setCopiedWord] = useState(null);
  const [viewMode, setViewMode] = useState(mode); // 'panel' or 'modal'

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setCopiedWord(null);
    }
  }, [isOpen]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const copyToClipboard = (word) => {
    navigator.clipboard.writeText(word);
    setCopiedWord(word);
    toast.success(`Copied "${word}" to clipboard`);
    setTimeout(() => setCopiedWord(null), 2000);
  };

  // Filter function for search
  const filterWords = (words) => {
    if (!searchTerm) return words;
    return words.filter(word => 
      word.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterCategory = (category, words) => {
    const filtered = filterWords(words);
    if (!searchTerm) return { show: true, words };
    if (filtered.length > 0) return { show: true, words: filtered };
    if (category.toLowerCase().includes(searchTerm.toLowerCase())) return { show: true, words };
    return { show: false, words: [] };
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'satisfied', label: 'Feelings (Met)', icon: Heart, color: 'text-green-400', bgColor: 'bg-green-500/20' },
    { id: 'unsatisfied', label: 'Feelings (Unmet)', icon: Heart, color: 'text-red-400', bgColor: 'bg-red-500/20' },
    { id: 'needs', label: 'Needs', icon: Leaf, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20' },
  ];

  const renderContent = () => (
    <>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-400" />
            Feelings & Needs Helper
          </h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setViewMode(viewMode === 'panel' ? 'modal' : 'panel')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
              title={viewMode === 'panel' ? 'Pop out to modal' : 'Dock to panel'}
            >
              {viewMode === 'panel' ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search feelings or needs..."
            className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-400"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  activeTab === tab.id 
                    ? `${tab.bgColor} ${tab.color} border border-current` 
                    : 'bg-slate-800 text-slate-400 hover:text-white border border-transparent'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 overflow-y-auto flex-1">
        {/* Steve's Wisdom - Compact */}
        <div className="mb-4 bg-amber-900/30 border border-amber-500/30 rounded-lg p-3">
          <p className="text-amber-200 text-xs leading-relaxed">
            <span className="text-amber-300 font-bold">💡 Steve's Tip:</span> Not sure of the words? <span className="text-amber-300">Read through the list</span> — your body will react to the words that matter.
          </p>
        </div>

        <p className="text-slate-400 text-xs mb-4 flex items-center gap-1">
          <Copy className="h-3 w-3" />
          Click any word to copy it
        </p>

        {/* Feelings When Satisfied */}
        {activeTab === 'satisfied' && (
          <div className="space-y-3">
            {Object.entries(ofnrData.feelingsWhenSatisfied).map(([category, words]) => {
              const { show, words: filteredWords } = filterCategory(category, words);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false; // Default expanded
              
              return (
                <div key={category} className="bg-slate-800/50 rounded-lg border border-green-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-green-300 uppercase text-sm">{category}</span>
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2">
                      {filteredWords.map((word) => (
                        <button
                          key={word}
                          onClick={() => copyToClipboard(word)}
                          className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                            copiedWord === word 
                              ? 'bg-green-500 text-white' 
                              : 'bg-green-900/30 text-green-200 hover:bg-green-800/50 border border-green-700/30'
                          }`}
                        >
                          {copiedWord === word ? <Check className="h-3 w-3 inline mr-1" /> : null}
                          {word}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Feelings When NOT Satisfied */}
        {activeTab === 'unsatisfied' && (
          <div className="space-y-3">
            {Object.entries(ofnrData.feelingsWhenNotSatisfied).map(([category, words]) => {
              const { show, words: filteredWords } = filterCategory(category, words);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false;
              
              return (
                <div key={category} className="bg-slate-800/50 rounded-lg border border-red-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-red-300 uppercase text-sm">{category}</span>
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2">
                      {filteredWords.map((word) => (
                        <button
                          key={word}
                          onClick={() => copyToClipboard(word)}
                          className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                            copiedWord === word 
                              ? 'bg-red-500 text-white' 
                              : 'bg-red-900/30 text-red-200 hover:bg-red-800/50 border border-red-700/30'
                          }`}
                        >
                          {copiedWord === word ? <Check className="h-3 w-3 inline mr-1" /> : null}
                          {word}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Needs */}
        {activeTab === 'needs' && (
          <div className="space-y-3">
            {Object.entries(ofnrData.needsByCategory).map(([category, needs]) => {
              const { show, words: filteredNeeds } = filterCategory(category, needs);
              if (!show) return null;
              
              const isExpanded = expandedCategories[category] !== false;
              
              return (
                <div key={category} className="bg-slate-800/50 rounded-lg border border-emerald-700/30 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold text-emerald-300 uppercase text-sm">{category}</span>
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2">
                      {filteredNeeds.map((need) => (
                        <button
                          key={need}
                          onClick={() => copyToClipboard(need)}
                          className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                            copiedWord === need 
                              ? 'bg-emerald-500 text-white' 
                              : 'bg-emerald-900/30 text-emerald-200 hover:bg-emerald-800/50 border border-emerald-700/30'
                          }`}
                        >
                          {copiedWord === need ? <Check className="h-3 w-3 inline mr-1" /> : null}
                          {need}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Non-Feelings Warning (shown at bottom of unsatisfied tab) */}
        {activeTab === 'unsatisfied' && (
          <div className="mt-6 bg-amber-900/20 border border-amber-700/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="font-semibold text-amber-300 text-sm">Watch Out: Non-Feeling Words</span>
            </div>
            <p className="text-amber-200/80 text-xs mb-3">
              These words blame others and give away your power. They are NOT feelings:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ofnrData.nonFeelingWords.slice(0, 15).map((word) => (
                <span key={word} className="px-2 py-0.5 bg-amber-900/40 text-amber-300 rounded text-xs border border-amber-700/30">
                  {word}
                </span>
              ))}
              <span className="px-2 py-0.5 text-amber-400 text-xs">...and more</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 p-3">
        <p className="text-slate-500 text-xs text-center">
          Use these to craft empathetic responses
        </p>
      </div>
    </>
  );

  // Panel View (slide-out from right)
  if (viewMode === 'panel') {
    return (
      <div 
        className={`fixed right-0 top-0 h-full w-96 bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {renderContent()}
      </div>
    );
  }

  // Modal View (centered overlay)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {renderContent()}
      </div>
    </div>
  );
}
